"use client"

import { useEffect } from "react"
import {
  datadogLogs,
  type LogsEvent,
  type LogsEventDomainContext,
} from "@datadog/browser-logs"
import { datadogRum } from "@datadog/browser-rum"

import { isDuplicateCspViolation } from "@/lib/csp-violation-dedup"
import { isLocalDevelopment } from "@/lib/utils"
import { type User } from "@/services/users/user.types"

/* 
https://docs.datadoghq.com/real_user_monitoring/guide/monitor-your-nextjs-app-with-rum/?tab=npm 
https://docs.datadoghq.com/real_user_monitoring/connect_rum_and_traces/?tab=browserrum#setup-rum 
*/

export const setDatadogUser = (user: User) => {
  const datadogUser = {
    id: user.uuid,
    tenant_uuid: user.tenant_uuid,
    account_id: user.account_id,
  }
  datadogRum.setUser(datadogUser)
  datadogLogs.setUser(datadogUser)
}

export const clearDatadogUser = () => {
  datadogRum.clearUser()
  datadogLogs.clearUser()
}

/**
 * Check if an error message is a benign ResizeObserver loop error.
 * This is a known browser warning that fires when a ResizeObserver callback
 * triggers a layout change causing another observation in the same animation frame.
 * It has no user-facing impact and does not indicate broken functionality.
 */
const isResizeObserverLoopError = (message?: string) =>
  message?.includes("ResizeObserver loop")

/**
 * Check if an error message carries an AbortError from an aborted signal.
 * Only reaches errors whose message holds the abort text (e.g. an unhandled
 * rejection); Browser Logs network errors need `isCallerCancelledRequest`.
 */
const isNextjsNavigationAbortError = (message?: string) =>
  message?.includes("signal is aborted without reason")

/**
 * Check if a Browser Logs network error is a fetch its caller aborted, e.g.
 * Next.js aborting in-flight RSC fetches and prefetches on `pagehide`, or a
 * search superseding its previous request (ET e224330a). The SDK logs these
 * as "Fetch error GET <url>" with the AbortError only in `error.stack`, so a
 * message check cannot see them. Timeouts must abort with a `TimeoutError`
 * reason (as `AbortSignal.timeout` does) to keep reporting.
 */
export const isCallerCancelledRequest = (
  { origin, error }: Pick<LogsEvent, "origin" | "error">,
  context: LogsEventDomainContext
) =>
  origin === "network" &&
  "isAborted" in context &&
  context.isAborted &&
  typeof error?.stack === "string" &&
  error.stack.startsWith("AbortError")

/**
 * Check if an error is the "Object Not Found Matching Id" noise thrown by
 * external browser automation/URL scanners (e.g. Outlook SafeLinks) probing
 * auth pages. No stack trace, thrown as a bare string, never from app code.
 */
export const isBrowserAutomationObjectNotFoundError = (message?: string) =>
  message?.includes("Object Not Found Matching Id:")

/**
 * Known hosts that can appear in browser-extension or injected widget CSP noise.
 * Keep this list narrow so genuine CSP regressions still surface in Datadog.
 */
const EXTENSION_NOISE_HOSTS = ["cdn.scite.ai"] as const

/**
 * Check if a CSP violation looks like extension or injected-widget noise.
 * We only suppress narrowly scoped font-src violations for known external hosts
 * or extension URL schemes, rather than muting CSP reports broadly.
 */
const isLikelyExtensionOrInjectedCspNoise = (message?: string) => {
  if (!message?.includes("blocked by 'font-src' directive")) return false

  return (
    message.includes("chrome-extension://") ||
    message.includes("moz-extension://") ||
    EXTENSION_NOISE_HOSTS.some((host) => message.includes(host))
  )
}

/**
 * The Datadog SDK renders a CSP report's violating script (`sourceFile`) as the
 * stack frame, e.g. `\n  at <anonymous> @ chrome-extension:12:93956`. Browsers
 * report extension scripts by scheme only, and Edge uses `chrome-extension`.
 * The SDK omits `:line` when the line is 0, so the scheme may end the frame.
 */
const EXTENSION_SOURCE_FRAME =
  /\n\s+at .* @ (?:chrome|moz|safari-web)-extension(?::|$)/

/**
 * Check if a CSP violation was raised by a browser-extension script rather than
 * app code, e.g. an extension injecting `<base href>` (blocked by `base-uri`)
 * or calling `eval` (blocked by `script-src`) on our pages (ET-3651). The
 * extension is named only in the stack, never in the message, so the
 * message-based check above cannot see it. Keyed on the violating script, not
 * the directive, so a violation from our own code still surfaces.
 */
export const isExtensionSourcedCspViolation = (
  message?: string,
  stack?: string
) =>
  !!message?.startsWith("csp_violation:") &&
  !!stack &&
  EXTENSION_SOURCE_FRAME.test(stack)

/**
 * Third-party analytics domains that don't allow Datadog tracing headers
 * (x-datadog-origin, etc.) in their CORS policy.
 */
const THIRD_PARTY_ANALYTICS_DOMAINS = [
  "segment.io",
  "segment.com",
  "intercom.io",
  "intercomcdn.com",
  "usepylon.com",
  "logrocket.com",
  "logrocket.io",
  "lr-in.com",
  "lr-ingest.com",
  "lr-ingest.io",
  "lr-intake.com",
  "ingest-lr.com",
  "intake-lr.com",
  "logr-ingest.com",
  "lrkt-in.com",
  "lgrckt-in.com",
] as const

/**
 * Check if a URL belongs to a third-party analytics service.
 * Returns true if the URL's hostname matches any of the excluded domains.
 */
const isThirdPartyAnalyticsUrl = (url: string): boolean => {
  try {
    const { hostname } = new URL(url)
    const host = hostname.toLowerCase()
    const matchesDomain = (domain: string) =>
      host === domain || host.endsWith(`.${domain}`)
    return THIRD_PARTY_ANALYTICS_DOMAINS.some((d) => matchesDomain(d))
  } catch {
    return false
  }
}

/**
 * Check if a Browser Logs network error is a third-party analytics request
 * that never got a response (status 0). That happens when the page is torn
 * down mid-request (email link scanners close signin pages ~1s after load) or
 * an ad blocker drops it; neither is actionable (ET-3473). Third-party 5xx
 * responses and all first-party network errors still report.
 */
export const isAbortedThirdPartyAnalyticsRequest = ({
  origin,
  http,
}: Pick<LogsEvent, "origin" | "http">) =>
  origin === "network" &&
  http?.status_code === 0 &&
  isThirdPartyAnalyticsUrl(http.url)

const AUTH_PAGE_PATHS = new Set(["/auth", "/auth/signin", "/auth/signup"])

/**
 * Check if a Browser Logs network error is an auth page's own server-action
 * POST that never got a response (status 0). The auth page calls server
 * actions on mount, and a server action POSTs to the page URL; email link
 * scanners and crawlers close the page ~1-2s after load, killing those POSTs
 * with "TypeError: Failed to fetch" (ET-3623). The SDK logs that whether or
 * not the app catches it, and it is no abort, so `isCallerCancelledRequest`
 * keeps it. This drops every no-response POST from these pages, including
 * email sign-in; a real user's failure stays visible as a RUM resource and,
 * for the mount-time actions, as an "[Auth] ... failed" warn log, but not in
 * Error Tracking.
 */
export const isTornDownAuthPagePost = (
  { origin, http }: Pick<LogsEvent, "origin" | "http">,
  pageOrigin: string
) => {
  if (origin !== "network" || http?.status_code !== 0 || http.method !== "POST")
    return false
  try {
    const url = new URL(http.url)
    return url.origin === pageOrigin && AUTH_PAGE_PATHS.has(url.pathname)
  } catch {
    return false
  }
}

/**
 * Google's crawlers render our pages with JavaScript cached from an older
 * deploy, so their server action calls carry IDs the live build no longer has
 * (ET-3628: ~99% of that issue in usprod). Matched on Google's documented
 * user-agent tokens, case-sensitive, so ordinary browsers never match.
 */
const GOOGLE_CRAWLER_USER_AGENT = /Googlebot|AdsBot-Google/

export const isGoogleCrawler = (userAgent: string) =>
  GOOGLE_CRAWLER_USER_AGENT.test(userAgent)

// TODO: Show example for manual error tracking https://docs.datadoghq.com/logs/log_collection/javascript/#error-tracking
// TODO: Scrub sensitive data from browser logs too when we do for backend https://docs.datadoghq.com/logs/log_collection/javascript/#scrub-sensitive-data-from-your-browser-logs
export function DatadogInit() {
  useEffect(() => {
    if (
      isLocalDevelopment() || // TODO: Comment this to instrument local webui on datadog
      // Crawler sessions are not users: send nothing and spend no RUM sessions.
      isGoogleCrawler(navigator.userAgent) ||
      !window.settings.DD_CLIENT_TOKEN ||
      !window.settings.DD_APPLICATION_ID
    ) {
      return
    }

    const domainEnvs: Record<string, string> = {
      "gotorhythms.ai": "dev1",
      "getrhythms.ai": "demo",
      "rhythms.ai": "usprod",
      "rhythms.dev": window.settings.DD_ENV,
    }
    const datadogInitConfig = {
      clientToken: window.settings.DD_CLIENT_TOKEN,
      site: "datadoghq.com",
      service: "webui-client",
      env: window.settings.DD_ENV,
      version: window.settings.DD_VERSION,
      sessionSampleRate: 100,
      trackingConsent: "granted", // FIXME: Though this is the default, may have to appropriately handle for GDPR later
      useSecureSessionCookie: !window.settings.ALLOW_INSECURE_COOKIES,
    } as const

    datadogLogs.init({
      ...datadogInitConfig,
      forwardErrorsToLogs: true,
      forwardConsoleLogs: "all",
      forwardReports: "all",
      beforeSend: (log, context) => {
        // Filter out benign ResizeObserver loop errors (RHY-4895)
        if (isResizeObserverLoopError(log.message)) return false
        // Filter out Next.js RSC navigation abort errors
        if (isNextjsNavigationAbortError(log.message)) return false
        if (isCallerCancelledRequest(log, context)) return false
        // Filter narrow CSP noise from extensions or injected third-party widgets
        if (isLikelyExtensionOrInjectedCspNoise(log.message)) return false
        if (isExtensionSourcedCspViolation(log.message, log.error?.stack))
          return false
        // Filter external browser-automation/URL-scanner noise on auth pages
        if (isBrowserAutomationObjectNotFoundError(log.message)) return false
        if (isAbortedThirdPartyAnalyticsRequest(log)) return false
        if (isTornDownAuthPagePost(log, window.location.origin)) return false
        // Report each distinct CSP violation at most once per browser
        if (isDuplicateCspViolation("logs", log.message)) return false
        return true
      },
    })
    datadogRum.init({
      ...datadogInitConfig,
      applicationId: window.settings.DD_APPLICATION_ID,
      sessionReplaySampleRate: 100,
      trackUserInteractions: true, // TODO: Enables the automatic collection of potentially sensitive or private data within user clicks in your application. To replace sensitive data https://docs.datadoghq.com/real_user_monitoring/browser/tracking_user_actions
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: "allow",
      // Specify URLs to propagate trace headers for connection between RUM and backend trace.
      // Uses a callback to allow our domains AND exclude third-party services.
      allowedTracingUrls: [
        (url: string): boolean => {
          if (isThirdPartyAnalyticsUrl(url)) return false
          try {
            const { hostname } = new URL(url)
            const host = hostname.toLowerCase()
            if (host === "localhost") return true
            const matchesDomain = (domain: string) =>
              host === domain || host.endsWith(`.${domain}`)
            return Object.keys(domainEnvs).some((d) => matchesDomain(d))
          } catch {
            return false
          }
        },
      ],
      // Exclude third-party analytics URLs from page activity tracking.
      excludedActivityUrls: [isThirdPartyAnalyticsUrl],
      beforeSend: (event) => {
        if (event.type === "error") {
          // Filter out benign ResizeObserver loop errors (RHY-4895)
          if (isResizeObserverLoopError(event.error?.message)) return false
          // Filter out Next.js RSC navigation abort errors
          if (isNextjsNavigationAbortError(event.error?.message)) return false
          // Filter narrow CSP noise from extensions or injected third-party widgets
          if (isLikelyExtensionOrInjectedCspNoise(event.error?.message))
            return false
          if (
            isExtensionSourcedCspViolation(
              event.error?.message,
              event.error?.stack
            )
          )
            return false
          // Filter external browser-automation/URL-scanner noise on auth pages
          if (isBrowserAutomationObjectNotFoundError(event.error?.message))
            return false
          // Report each distinct CSP violation at most once per browser
          if (isDuplicateCspViolation("rum", event.error?.message)) return false
        }
        return true
      },
    })
  }, []) // Empty dependency array means this runs once on mount

  // Render nothing - this component is only included so that the init code above will run client-side
  return null
}
